import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Fingerprint, Trash2, ShieldCheck, Loader2, Smartphone, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";

const panel = "bg-white rounded-xl border border-slate-200 p-6";

export default function LoginSettingsTab({ user, f, set, save }) {
  const [status, setStatus] = useState({ allowed: true, enrolled: 0 });
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState([]);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const loadCreds = () => {
    api.get("/webauthn/credentials").then((r) => setCreds(r.data)).catch(() => {});
    api.get("/webauthn/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadCreds();
    if (isAdmin) api.get("/staff").then((r) => setStaff((r.data || []).filter((s) => s.role !== "admin"))).catch(() => {});
  }, []);  // eslint-disable-line

  const enroll = async () => {
    setBusy(true);
    try {
      const opts = (await api.post("/webauthn/register/options")).data;
      const cred = await startRegistration({ optionsJSON: opts });
      await api.post("/webauthn/register/verify", cred);
      toast.success("Fingerprint / Face sign-in enabled on this device");
      loadCreds();
    } catch (e) {
      const msg = e?.name === "NotAllowedError" ? "Cancelled or timed out — try again." : (apiErr(e?.response?.data?.detail) || e.message || "Could not enable biometric here");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/webauthn/credentials/${id}`); loadCreds(); toast.success("Device removed"); }
    catch { toast.error("Failed to remove"); }
  };

  const toggleStaff = async (s, enabled) => {
    try {
      await api.patch(`/staff/${s.id}/field`, { field: "alt_login_disabled", value: !enabled });
      setStaff((p) => p.map((x) => x.id === s.id ? { ...x, alt_login_disabled: !enabled } : x));
    } catch { toast.error("Failed to update"); }
  };

  const toggleMaster = async (v) => { set("alt_login_enabled", v); try { await save(); } catch { /* save shows toast */ } };
  const masterOn = f?.alt_login_enabled !== false;

  return (
    <div className="space-y-6">
      {/* Biometric on this device */}
      <div className={panel} data-testid="login-biometric-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0"><Fingerprint size={22} /></div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Fingerprint / Face sign-in</h3>
            <p className="text-sm text-slate-500">Use this device's built-in fingerprint or face unlock to sign in next time. Set it up once per device.</p>
            {!status.allowed ? (
              <p className="mt-3 text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">Your administrator has turned off fingerprint & OTP sign-in for your account.</p>
            ) : (
              <>
                <Button onClick={enroll} disabled={busy} className="mt-4" data-testid="enroll-fingerprint-btn">
                  {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Fingerprint size={16} className="mr-2" />}Enable fingerprint on this device
                </Button>
                <div className="mt-4 space-y-2">
                  {creds.length === 0 && <p className="text-sm text-slate-400">No devices enrolled yet.</p>}
                  {creds.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`cred-row-${c.id}`}>
                      <div className="flex items-center gap-2 text-sm text-slate-700"><Smartphone size={16} className="text-slate-400" />{c.device_label}
                        {c.last_used_at && <span className="text-xs text-slate-400 ml-1">· last used {String(c.last_used_at).slice(0, 10)}</span>}</div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => remove(c.id)} data-testid={`remove-cred-${c.id}`}><Trash2 size={15} /></Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Admin controls */}
      {isAdmin && (
        <div className={panel} data-testid="login-admin-card">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0"><ShieldCheck size={22} /></div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-slate-900">Who can use Fingerprint & OTP login</h3>
              <p className="text-sm text-slate-500">These extra sign-in methods are for Admins and staff. Turn them on for everyone, then switch off any individual staff member below.</p>

              <label className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 cursor-pointer">
                <span className="text-sm font-medium text-slate-700 flex items-center gap-2"><KeyRound size={16} className="text-indigo-500" />Allow Fingerprint & OTP for all staff</span>
                <Switch checked={masterOn} onCheckedChange={toggleMaster} data-testid="alt-login-master-switch" />
              </label>

              {masterOn && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Per-staff access</div>
                  <div className="space-y-2 max-h-80 overflow-auto">
                    {staff.length === 0 && <p className="text-sm text-slate-400">No staff members yet.</p>}
                    {staff.map((s) => (
                      <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`staff-altlogin-${s.id}`}>
                        <div className="text-sm text-slate-700">{s.name} <span className="text-xs text-slate-400">· {s.designation || s.role}</span></div>
                        <Switch checked={!s.alt_login_disabled} onCheckedChange={(v) => toggleStaff(s, v)} data-testid={`staff-altlogin-switch-${s.id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
